import { GetUserParticipationEventsByReadingQuery } from '../generated/graphql'
import { HLO } from '../types/global'

export type Meaning = 'registered' | 'pending-invite' | 'isPresent'

export type Results = { [M in Meaning]: boolean }

type ProcessedEvent = {
  name: HLO.ParticipationState
  created: Date
}

const statePriority: Partial<Record<HLO.ParticipationState, number>> = {
  signup: 1,
  invite: 1,
  join: 2,
  decline: 3,
  cancel: 3,
  leave: 3,
  absent: 3,
}

export type Options = {
  events: GetUserParticipationEventsByReadingQuery['participationevent']
}

export default function resolveParticipationEventMeaning({
  events,
}: Options): Results {
  const processedEvents = processEvents(events)

  const indexes = {
    signup: indexOfEvent(processedEvents, 'signup'),
    join: indexOfEvent(processedEvents, 'join'),
    cancel: indexOfEvent(processedEvents, 'cancel'),
    leave: indexOfEvent(processedEvents, 'leave'),
    absent: indexOfEvent(processedEvents, 'absent'),
    invite: indexOfEvent(processedEvents, 'invite'),
    decline: indexOfEvent(processedEvents, 'decline'),
  }

  const didSignUp = indexes.signup !== -1
  const didJoin = indexes.join !== -1
  const didCancel = indexes.cancel !== -1
  const hasInvite = indexes.invite !== -1
  const didDecline = indexes.decline !== -1

  const results: Results = {
    registered: false,
    isPresent: false,
    'pending-invite': false,
  }
  if (didSignUp) {
    results['registered'] = true
  }

  if (didSignUp && didCancel && indexes.cancel < indexes.signup) {
    results['registered'] = false
  }

  if (didJoin) {
    results['isPresent'] = true
  }

  if (didJoin && didCancel && indexes.cancel < indexes.join) {
    results['isPresent'] = false
  }

  if (hasInvite) {
    results['pending-invite'] = true
  }

  const endedPresence = [indexes.leave, indexes.absent].some(
    (i) => i !== -1 && didJoin && i < indexes.join,
  )
  if (endedPresence) {
    results['isPresent'] = false
  }

  if (hasInvite && didDecline && indexes.decline < indexes.invite) {
    results['pending-invite'] = false
  }

  return results
}

export function processEvents(events: Options['events']) {
  const eventsCopy = [...events]

  const withTypes: ProcessedEvent[] = eventsCopy.map((e) => ({
    name: e.participationstate.name as HLO.ParticipationState,
    created: new Date(e.created),
  }))

  // On identical timestamps, later lifecycle states win
  const mostRecentFirst = withTypes.sort(
    (e1, e2) =>
      Number(e2.created) - Number(e1.created) ||
      (statePriority[e2.name] ?? 0) - (statePriority[e1.name] ?? 0),
  )

  return mostRecentFirst
}

export function indexOfEvent(
  events: ProcessedEvent[],
  event: HLO.ParticipationState,
) {
  return events.findIndex(({ name }) => name === event)
}
